import { Form, message } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getCurrentPattern } from "../../../../commons/constants";
import {
  MERCHANT_ACOUNT_INFORMATION,
  MERCHANT_REQUEST_UPDATE_ACCOUNT_INFORMATION,
} from "../../../../commons/constants/api-urls";
import { objecFieldsRejected } from "../../../../commons/constants/user";
import defaultAxios from "../../../../commons/utils/axios";
import {
  mapAccountInformationWhenHasNoUpdateRequest,
  mapAccountInformationWhenHasUpdateRequest,
} from "../../../../commons/utils/functions/accountInformationMapping";
import { STATUS_CODE } from "../../../Notification/Notification";

const useUpdateAccountInformation = () => {
  const navigate = useNavigate();
  const [statusUpdate, setStatusUpdate] = useState();
  const [data, setData] = useState<any>();
  const [loading, setLoading] = useState(false);
  const [openConfirmPopup, setOpenConfirmPopup] = useState(false);
  const [form] = Form.useForm<AccountInformationForm>();
  const [fieldsChanged, setFieldsChanged] = useState<any>([]); // store fields changed
  const [initialValuesForm, setInitialValuesForm] = useState<any>({});

  async function handleUpdateAccountInformation(body: UpdateAccountInformationBody) {
    setLoading(true);
    try {
      await defaultAxios.put(MERCHANT_REQUEST_UPDATE_ACCOUNT_INFORMATION(), body);
      message.success("Send Request Update successfully");
      navigate("/account-information");
    } catch (error) {
      console.log(error);
      message.error("Send Request Update failed");
    } finally {
      setLoading(false);
    }
  }

  function handleCheckIsDocumentsIsEmpty() {
    const documentsFromData = data?.information?.merchantApprovals?.documents || data?.information?.documents;
    const documentsFromForm = form.getFieldValue("new_documents");

    if (!documentsFromForm && documentsFromData.length === 0) {
      return true;
    }
    if (documentsFromData.length === 0 && documentsFromForm.length === 0) {
      return true;
    }
    return false;
  }

  function handleCreateObjectFormTouched(values: AccountInformationForm) {
    const arrayOfKeyFieldChanged = Object.keys(values).filter(item => fieldsChanged.includes(item));

    //this to make sure always display update phone and region together
    if (arrayOfKeyFieldChanged.includes("phone") && !arrayOfKeyFieldChanged.includes("region")) {
      arrayOfKeyFieldChanged.push("region");
    } else if (arrayOfKeyFieldChanged.includes("region") && !arrayOfKeyFieldChanged.includes("phone")) {
      arrayOfKeyFieldChanged.push("phone");
    }

    const newObjectForm: any = {};
    arrayOfKeyFieldChanged.forEach((item: any) => {
      newObjectForm[item] = values[item as keyof AccountInformationForm];
    });
    return newObjectForm;
  }

  function handleAppendForm(values: AccountInformationForm) {
    const newObjectForm = handleCreateObjectFormTouched(values);
    const formData: any = new FormData();

    if (values["new_documents"]?.length > 0) {
      values["new_documents"].forEach((item: any) => {
        formData.append("new_documents", item.originFileObj);
      });
    }

    Object.keys(newObjectForm).forEach(key => {
      if (key === "region") {
        formData.append("phoneCode", values.region.replace("+", ""));
      }
      if (
        newObjectForm[key as keyof AccountInformationForm] &&
        newObjectForm[key as keyof AccountInformationForm] !== initialValuesForm[key]
      ) {
        formData.append(key, values[key as keyof AccountInformationForm]);
      }
    });

    // if (data?.information?.merchantApprovals?.documents.length > 0) {
    //   formData.append("documents", JSON.stringify(data.information.merchantApprovals.documents));
    // }

    if (data?.information?.documents.length > 0) {
      formData.append("documents", JSON.stringify(data.information.documents));
    }

    return formData;
  }

  function handleCancel() {
    navigate("/account-information");
  }

  const getUserInfo = async () => {
    try {
      const res = await defaultAxios.get(MERCHANT_ACOUNT_INFORMATION());
      if (res.data && res.status === STATUS_CODE.SUCCESS) {
        setData(res.data);
        setStatusUpdate(res.data.information.merchantApprovals.status);
      }
    } catch (error) {}
  };

  function handleTrackFieldsTouched(fieldName: string) {
    setFieldsChanged((prev: string[]) => {
      if (!prev.includes(fieldName)) {
        return [...prev, fieldName];
      }
      return prev;
    });
  }

  function handleOpenModal() {
    setOpenConfirmPopup(true);
  }

  function handleCancelModal() {
    setOpenConfirmPopup(false);
  }

  function submitChange(values: AccountInformationForm) {
    setOpenConfirmPopup(false);
    if (handleCheckIsDocumentsIsEmpty()) {
      message.error("Please upload documents");
      return;
    }
    const formData = handleAppendForm(values);
    handleUpdateAccountInformation(formData);
  }

  const region = Form.useWatch("region", form);
  const phonePattern = useMemo(() => getCurrentPattern(region), [region]);

  useEffect(() => {
    getUserInfo();
  }, []);

  useEffect(() => {
    if (data?.information?.merchantApprovals) {
      // set form error for invalid fields
      const formValue = mapAccountInformationWhenHasUpdateRequest(data);
      form.setFieldsValue(formValue);
      setInitialValuesForm(formValue);
      if (data.information.merchantApprovals.rejectedFields?.length > 0)
        form.setFields(
          data.information.merchantApprovals.rejectedFields.map((key: string) => ({
            name: objecFieldsRejected[key as keyof typeof objecFieldsRejected],
            errors: [""],
          }))
        );
      return;
    }
    if (data) {
      const formValue = mapAccountInformationWhenHasNoUpdateRequest(data);
      form.setFieldsValue(formValue);
      setInitialValuesForm(formValue);
    }
  }, [data]);

  return {
    handleUpdateAccountInformation,
    handleCheckIsDocumentsIsEmpty,
    handleAppendForm,
    handleCancel,
    handleTrackFieldsTouched,
    phonePattern,
    loading,
    form,
    statusUpdate,
    data,
    openConfirmPopup,
    initialValuesForm,
    handleOpenModal,
    setData,
    submitChange,
    handleCancelModal,
  };
};

export default useUpdateAccountInformation;
